import {
  type FC, useMemo, useState,
} from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import {
  DropdownLabelComponent, type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { StackedWarningsComponent } from '~/_shared/baseComponents/warning/stackedWarnings.component';
import {
  headingStyle, optionsWrapperStyle, submitButtonStyle,
} from '~/_shared/components/customizeMetrics/selectData/customizeMetricsSelectData.styles';
import { compareSpreadsheetColumns } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { useGetColumnATOMetrics } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/useGetColumnATOMetrics';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { type SpreadsheetColumn } from '../../../types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '../../../utils/hooks';
import { MetricsDataAction } from '../../../utils/metric/metrics.types';
import { OverlayLoaderComponent } from '../../overlay/overlayLoader.component';
import { customizeMetricsDropdownTriggerStyle } from '../customizeMetrics.component';
import {
  getDataActionOptions, requiresNumericalBaseMetricColumn,
} from '../customizeMetrics.helpers';
import {
  type SelectedDataMetric, SelectedDataType,
} from '../selectedDataMetric.type';

type CustomizeMetricsSelectDataMetricsProps = Readonly<{
  addSelectionButtonText: string;
  className?: string;
  columnDataSources: ReadonlyArray<SpreadsheetColumn>;
  onSubmit: (newData: SelectedDataMetric[]) => void;
  selectedData: ReadonlyArray<SelectedDataMetric>;
}>;

const matchesCurrentSelection = (
  item: SelectedDataMetric,
  column: SpreadsheetColumn,
  action: MetricsDataAction,
  secondaryColumn: SpreadsheetColumn | undefined,
): boolean => {
  if (item.type !== SelectedDataType.SpreadsheetColumn) {
    return false;
  }

  if (!compareSpreadsheetColumns(item.primarySpreadsheetColumn, column) || item.dataAction !== action) {
    return false;
  }

  if (item.dataAction === MetricsDataAction.GROUP_TOTAL) {
    if (!secondaryColumn) {
      return false;
    }

    return compareSpreadsheetColumns(item.secondarySpreadsheetColumn, secondaryColumn);
  }

  return true;
};

export const CustomizeMetricsSelectDataMetricsComponent: FC<CustomizeMetricsSelectDataMetricsProps> = (props) => {
  const [primarySpreadsheetColumnId, setPrimarySpreadsheetColumnId] = useState<string | null>(null);
  const [secondarySpreadsheetColumnId, setSecondarySpreadsheetColumnId] = useState<string | null>(null);
  const [dataAction, setDataAction] = useState<MetricsDataAction | null>(null);
  const [t] = useTranslation();
  const spreadsheetId = usePrimarySpreadsheetId();

  const { columnMetrics, isLoading: isLoadingColumnMetrics } = useGetColumnATOMetrics(spreadsheetId, { includeGroups: false });

  const { columnDataSources, addSelectionButtonText, selectedData, onSubmit } = props;

  const dataActionOptions: DropdownOption<MetricsDataAction>[] = useMemo(() => getDataActionOptions(t), [t]);

  const columnNameOptions: DropdownOption<string>[] = useMemo(() => columnDataSources.map((dataSource) => ({
    value: dataSource.id,
    name: dataSource.name,
  })), [columnDataSources]);

  const primarySpreadsheetColumn = columnDataSources.find(column => column.id === primarySpreadsheetColumnId);
  const secondarySpreadsheetColumn = columnDataSources.find(column => column.id === secondarySpreadsheetColumnId);

  const isItemAlreadySelected = useMemo(() => (
    !primarySpreadsheetColumn || !dataAction
      ? false
      : selectedData.some(
        item => matchesCurrentSelection(item, primarySpreadsheetColumn, dataAction, secondarySpreadsheetColumn)
      )
  ), [dataAction, secondarySpreadsheetColumn, selectedData, primarySpreadsheetColumn]);

  const primaryColumnMetric = columnMetrics.find(c => c.columnId === primarySpreadsheetColumnId);
  const isPrimaryColumnNumerical = primaryColumnMetric?.isNumeric;

  const secondaryColumnMetric = columnMetrics.find(c => c.columnId === secondarySpreadsheetColumnId);
  const isSecondaryColumnNumerical = secondaryColumnMetric?.isNumeric;

  const selectedDataActionRequiresNumerical =
    dataAction !== null &&
    requiresNumericalBaseMetricColumn(dataAction);

  const isMissingRequiredSelection = !dataAction || !primarySpreadsheetColumnId
    || (dataAction === MetricsDataAction.GROUP_TOTAL && !secondarySpreadsheetColumnId);

  const isInvalidNumericSelection = selectedDataActionRequiresNumerical && !isPrimaryColumnNumerical;
  const isInvalidSecondaryColumnSelection = dataAction === MetricsDataAction.GROUP_TOTAL
    && !!secondarySpreadsheetColumnId && !isSecondaryColumnNumerical;

  const isSubmitDisabled = isMissingRequiredSelection || isInvalidNumericSelection || isInvalidSecondaryColumnSelection || isItemAlreadySelected;

  const addSelectedItem = () => {
    if (isSubmitDisabled || !primarySpreadsheetColumn || !dataAction) {
      return;
    }

    let dataItem: SelectedDataMetric;
    if (dataAction === MetricsDataAction.GROUP_TOTAL) {
      if (!secondarySpreadsheetColumn) {
        return;
      }
      dataItem = {
        type: SelectedDataType.SpreadsheetColumn,
        dataAction,
        primarySpreadsheetColumn,
        secondarySpreadsheetColumn,
      };
    }
    else {
      dataItem = {
        type: SelectedDataType.SpreadsheetColumn,
        dataAction,
        primarySpreadsheetColumn,
      };
    }

    const newData = [...props.selectedData];
    newData.push(dataItem);

    onSubmit(newData);

    setDataAction(null);
    setPrimarySpreadsheetColumnId(null);
    setSecondarySpreadsheetColumnId(null);
  };

  const metricsWarnings = useMemo(() => {
    const warnings = [];
    if (isInvalidNumericSelection || isInvalidSecondaryColumnSelection) {
      warnings.push(t('notNumericalColumnWarning'));
    }
    if (isItemAlreadySelected) {
      warnings.push(t('This combination is already on the list'));
    }

    return warnings;
  }, [isInvalidSecondaryColumnSelection, isInvalidNumericSelection, isItemAlreadySelected, t]);

  return (
    <div className={props.className}>
      {isLoadingColumnMetrics && <OverlayLoaderComponent />}
      <strong css={headingStyle}>{t('Data Metrics')}</strong>

      <div css={optionsWrapperStyle}>
        <div>
          <DropdownLabelComponent>{t('Select Column Name')}</DropdownLabelComponent>
          <RegularDropdownComponent
            onChange={setPrimarySpreadsheetColumnId}
            value={primarySpreadsheetColumnId}
            options={columnNameOptions}
            triggerStyle={customizeMetricsDropdownTriggerStyle({ isPlaceholder: primarySpreadsheetColumnId === null })}
            placeholder={t('Select Column Name')}
          />
        </div>

        <div>
          <DropdownLabelComponent>{t('Data Action')}</DropdownLabelComponent>
          <RegularDropdownComponent
            onChange={setDataAction}
            value={dataAction}
            options={dataActionOptions}
            triggerStyle={customizeMetricsDropdownTriggerStyle({ isPlaceholder: dataAction === null })}
            placeholder={t('Data Action')}
          />
        </div>

        {dataAction === MetricsDataAction.GROUP_TOTAL && (
          <div>
            <DropdownLabelComponent>{t('Select Value Column')}</DropdownLabelComponent>
            <RegularDropdownComponent
              onChange={setSecondarySpreadsheetColumnId}
              value={secondarySpreadsheetColumnId}
              options={columnNameOptions}
              triggerStyle={customizeMetricsDropdownTriggerStyle({ isPlaceholder: dataAction === null })}
              placeholder={t('Select Value Column')}
            />
          </div>
        )}

        <StackedWarningsComponent warnings={metricsWarnings} />
      </div>

      <ButtonComponent
        text={addSelectionButtonText}
        onClick={addSelectedItem}
        css={submitButtonStyle}
        isDisabled={isSubmitDisabled}
      />
    </div>
  );
};
